import type { Balance } from '@runkr/config'
import type { Aabb, CompiledTrack, Platform } from '../track/types'
import { NEAR_PAD_PX, nearby } from '../track/xindex'
import type { PlayerState, World } from '../world'

/**
 * The compiled-track entries a bot can meet in the next few seconds, fetched once per decision
 * from the x-bucket index (`nearby`) and narrowed per rollout to what that rollout can reach.
 */
export interface Scene {
  readonly solids: readonly number[]
  readonly platforms: readonly number[]
  readonly ramps: readonly number[]
  readonly zones: readonly number[]
  readonly hazards: readonly number[]
  /** Largest surface `y` in range (the lowest floor): falling past it means nothing can catch. */
  readonly lowestTop: number
  /** Longest rollout the scene covers, in ticks. */
  readonly horizon: number
}

/** Ticks in the air for a full jump plus a full double jump, landing at take-off height. */
export function airTicks(cfg: Balance): number {
  const up = cfg.jumpVelocityPxPerTick + cfg.doubleJumpVelocityPxPerTick
  return Math.ceil((2 * up) / cfg.gravityPxPerTick2)
}

/** Fastest horizontal speed `p` can have during a rollout (base plus any boost). */
export function maxSpeed(cfg: Balance, p: PlayerState): number {
  const boost = Math.max(p.boostPct, cfg.items.boostJet.speedPct, cfg.speedStripSpeedPct)
  return (cfg.baseSpeedPxPerTick * (100 + boost)) / 100
}

/** Slowest running speed (base speed in mud). */
export function minSpeed(cfg: Balance): number {
  return (cfg.baseSpeedPxPerTick * cfg.mudSpeedPct) / 100
}

/** `[x0, x1]` a rollout of `horizon` ticks from player `i` can touch, padded like `nearBody`. */
function reach(world: World, i: number, horizon: number): [number, number] {
  const p = world.players[i]!
  return [p.x - NEAR_PAD_PX, p.x + p.w + horizon * maxSpeed(world.cfg, p) + NEAR_PAD_PX]
}

/** A moving platform's sweep, like `platformSpan` in the index. */
function platformBox(pl: Platform): Aabb {
  if (pl.kind !== 'moving') return pl
  return { x: Math.min(pl.x, pl.x + pl.dx), y: pl.y, w: pl.w + Math.abs(pl.dx), h: pl.h }
}

function lowestTopOf(track: CompiledTrack, s: Omit<Scene, 'lowestTop' | 'horizon'>): number {
  let low = -Infinity
  for (const k of s.solids) low = Math.max(low, track.solids[k]!.y)
  for (const k of s.platforms) {
    const pl = track.platforms[k]!
    low = Math.max(low, pl.kind === 'moving' ? pl.y + Math.max(0, pl.dy) : pl.y)
  }
  for (const k of s.ramps) low = Math.max(low, track.ramps[k]!.y + track.ramps[k]!.h)
  return low === -Infinity ? track.killY : Math.min(low, track.killY)
}

function make(
  track: CompiledTrack,
  lists: Omit<Scene, 'lowestTop' | 'horizon'>,
  horizon: number,
): Scene {
  return { ...lists, lowestTop: lowestTopOf(track, lists), horizon }
}

/** Entries around player `i` for rollouts of up to `horizon` ticks. */
export function buildScene(world: World, i: number, horizon: number): Scene {
  const { track } = world
  const [x0, x1] = reach(world, i, horizon)
  const ix = track.index
  return make(
    track,
    {
      solids: nearby(ix.solids, x0, x1),
      platforms: nearby(ix.platforms, x0, x1),
      ramps: nearby(ix.ramps, x0, x1),
      zones: nearby(ix.zones, x0, x1),
      hazards: nearby(ix.hazards, x0, x1),
    },
    horizon,
  )
}

/**
 * The part of `scene` a rollout of `horizon` ticks can reach (exact spans, not buckets), so each
 * simulated tick tests only nearby entries.
 */
export function narrow(world: World, scene: Scene, i: number, horizon: number): Scene {
  const { track } = world
  const h = Math.min(horizon, scene.horizon)
  const [x0, x1] = reach(world, i, h)
  const keep = (ks: readonly number[], boxOf: (k: number) => Aabb): number[] =>
    ks.filter((k) => {
      const a = boxOf(k)
      return a.x < x1 && a.x + a.w > x0
    })
  return make(
    track,
    {
      solids: keep(scene.solids, (k) => track.solids[k]!),
      platforms: keep(scene.platforms, (k) => platformBox(track.platforms[k]!)),
      ramps: keep(scene.ramps, (k) => track.ramps[k]!),
      zones: keep(scene.zones, (k) => track.zones[k]!),
      hazards: keep(scene.hazards, (k) => track.hazards[k]!),
    },
    h,
  )
}
